import { gql } from '@apollo/client'

/**
 * Roleplay action results — a learner holds a conversation with an AI character and is scored against the
 * creator's criteria. The API keeps every attempt; the study row carries this summary of them.
 *
 * No transcripts come over GraphQL. Reading what a learner actually said is the REST reporting endpoint
 * (`GET /api/v2/studies/roleplay/results/:companyId/:courseId/:actionId/:userId`).
 */

export type RoleplayCriterionScore = {
  criterionId: string,
  criterionText: string,
  /** 1–5; 0 means the criterion never came up in the conversation. */
  score: number,
  reason: string,
}

export type RoleplayResult = {
  // The attempt this study row describes.
  sessionId: string,
  attemptNum: number,
  messageCount: number,
  activeSeconds?: number|null,
  /** 'reset' after a learner's "try again": the action is not done, the history below still holds. */
  endedReason: string,
  averageScore?: number|null,
  scores?: RoleplayCriterionScore[]|null,

  // History across every attempt at this action.
  attemptsCount: number,
  resetCount: number,
  firstPlayedAt?: number|null,
  firstCompletedAt?: number|null,
  firstAverageScore?: number|null,
  lastPlayedAt?: number|null,
  lastCompletedAt?: number|null,
  lastAverageScore?: number|null,
  bestAverageScore?: number|null,
  lastResetAt?: number|null,
  lastResetBy?: string|null,
}

/**
 * The `roleplayResult { … }` selection, as a plain field list rather than a named fragment: it is embedded in the
 * Study fragment and in the responses query, and a named fragment included twice in one document is an error.
 */
export const ROLEPLAY_RESULT_FIELDS = `
  roleplayResult {
    sessionId
    attemptNum
    messageCount
    activeSeconds
    endedReason
    averageScore
    scores {
      criterionId
      criterionText
      score
      reason
    }
    attemptsCount
    resetCount
    firstPlayedAt
    firstCompletedAt
    firstAverageScore
    lastPlayedAt
    lastCompletedAt
    lastAverageScore
    bestAverageScore
    lastResetAt
    lastResetBy
  }
`

export type RoleplayAttempt = {
  sessionId: string,
  attemptNum: number,
  status: string,
  startedAt: number,
  endedAt?: number|null,
  endedReason?: string|null,
  messageCount: number,
  activeSeconds?: number|null,
  averageScore?: number|null,
  scores?: RoleplayCriterionScore[]|null,
  /** Set when the learner (or a reset) replaced this attempt. It is kept, with its scores. */
  supersededAt?: number|null,
  supersededBy?: string|null,
}

export type GetRoleplayAttemptsVars = {
  companyId: string,
  courseId: string,
  actionId: string,
  userId: string,
}
export type GetRoleplayAttemptsRes = {attempts: RoleplayAttempt[]}

export const GET_ROLEPLAY_ATTEMPTS = gql`
  query GetRoleplayAttempts($companyId: String!, $courseId: String!, $actionId: String!, $userId: String!) {
    attempts: getRoleplayAttempts(companyId: $companyId, courseId: $courseId, actionId: $actionId, userId: $userId) {
      sessionId
      attemptNum
      status
      startedAt
      endedAt
      endedReason
      messageCount
      activeSeconds
      averageScore
      scores {
        criterionId
        criterionText
        score
        reason
      }
      supersededAt
      supersededBy
    }
  }
`

/** "7m 12s" from seconds; empty for nothing. */
export function formatRoleplayDuration(seconds: number|null|undefined): string {
  if (!seconds) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  return [h ? `${h}h` : '', m ? `${m}m` : '', s ? `${s}s` : ''].filter(Boolean).join(' ')
}

/** Scores are out of 5, to one decimal. */
export function formatRoleplayScore(score: number|null|undefined): string {
  return score == null ? '' : `${score.toFixed(1)}/5`
}
